import * as Sentry from '@sentry/react'
import { config } from '../config/env'
import { logger } from './logger'

/**
 * Initialize Sentry error tracking
 * 
 * Environment-specific behavior:
 * - Development: Disabled by default, can be enabled for testing
 * - Staging: Enabled, captures fatal errors only by default
 * - Production: Enabled, captures all error types
 */
export const initializeSentry = () => {
  // Check if Sentry should be initialized
  if (!config.sentryDsn) {
    logger.debug('⚠️ Sentry DSN not configured, skipping initialization')
    return
  }

  if (!config.sentryEnabled) {
    logger.debug('⚠️ Sentry is disabled for this environment')
    return
  }

  // Determine beforeSend filter based on log level
  // Level priority works as a threshold: captures the configured level AND everything more severe
  // Example: If logLevel = "warning" (priority 2), it will capture warning, error, and fatal
  //          but will filter out debug and info
  const beforeSend = (event, hint) => {
    const logLevel = config.sentryLogLevel.toLowerCase()
    const eventLevel = event.level || 'error'
    
    // Filter based on configured log level (threshold system)
    const levelPriority = {
      debug: 0,    // Captures: debug, info, warning, error, fatal
      info: 1,     // Captures: info, warning, error, fatal
      warning: 2,  // Captures: warning, error, fatal
      error: 3,    // Captures: error, fatal
      fatal: 4     // Captures: fatal only
    }
    
    const configuredPriority = levelPriority[logLevel] || 2
    const eventPriority = levelPriority[eventLevel] || 3
    
    // Only send events that meet or exceed the configured priority threshold
    if (eventPriority < configuredPriority) {
      logger.debug(`🚫 Sentry: Filtered ${eventLevel} event (threshold: ${logLevel})`)
      return null
    }
    
    return event
  }

  try {
    Sentry.init({
      dsn: config.sentryDsn,
      environment: config.sentryEnvironment,
      release: config.sentryRelease,
      
      // Performance Monitoring
      tracesSampleRate: config.sentryTracesSampleRate,
      
      // Filter events based on log level
      beforeSend,
      
      // Integrations - using default integrations
      // BrowserTracing is automatically included in @sentry/react
      integrations: function(integrations) {
        // Filter out any problematic integrations if needed
        return integrations.filter(integration => {
          // Keep all default integrations
          return true;
        });
      },
      
      // Performance Monitoring - trace propagation targets
      tracePropagationTargets: [
        'localhost',
        /^https:\/\/.*\.sunlight\.com/,
        /^https:\/\/quiz\.sunlight\.com/
      ],
      
      // Session Replay sample rates - Commented out (not using Replay for now)
      // replaysSessionSampleRate: config.isProd ? 0.1 : 0,
      // replaysOnErrorSampleRate: config.isProd ? 1.0 : 0,
      
      // Additional context
      initialScope: {
        tags: {
          funnel_version: '1.0.0',
        },
      },
    })

    logger.debug(`✅ Sentry initialized (env: ${config.sentryEnvironment}, level: ${config.sentryLogLevel})`)
  } catch (error) {
    logger.error('❌ Failed to initialize Sentry:', error)
  }
}

/**
 * Manually capture an exception
 * @param {Error} error - The error to capture
 * @param {Object} context - Additional context
 */
export const captureException = (error, context = {}) => {
  if (!config.sentryEnabled) return
  
  Sentry.captureException(error, {
    tags: context.tags || {},
    extra: context.extra || {},
    level: context.level || 'error',
  })
}

/**
 * Manually capture a message
 * @param {string} message - The message to capture
 * @param {string} level - The severity level
 * @param {Object} context - Additional context
 */
export const captureMessage = (message, level = 'info', context = {}) => {
  if (!config.sentryEnabled) return
  
  Sentry.captureMessage(message, {
    level,
    tags: context.tags || {},
    extra: context.extra || {},
  })
}

/**
 * Set user context for error tracking
 * @param {Object} user - User information
 */
export const setUserContext = (user) => {
  if (!config.sentryEnabled) return
  
  Sentry.setUser({
    id: user.id || user.entry_id,
    email: user.email,
    // Do not send name or other sensitive PHI
  })
}

/**
 * Add breadcrumb for debugging
 * @param {Object} breadcrumb - Breadcrumb data
 */
export const addBreadcrumb = (breadcrumb) => {
  if (!config.sentryEnabled) return
  
  Sentry.addBreadcrumb({
    category: breadcrumb.category || 'custom',
    message: breadcrumb.message,
    level: breadcrumb.level || 'info',
    data: breadcrumb.data || {},
  })
}

export default {
  initializeSentry,
  captureException,
  captureMessage,
  setUserContext,
  addBreadcrumb,
}
